import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, Package2, Shield, Clock, MessageCircle, CheckCircle2, Rocket, BookOpen, Bot, Building2, Users, Globe2, Calculator } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import YuanRateCard from './YuanRateCard';
import RequestForm from './services/RequestForm';

function HomePage() {
  const [expandedFaqs, setExpandedFaqs] = useState<Set<number>>(new Set([0]));
  const navigate = useNavigate();
  const [showRequestForm, setShowRequestForm] = useState(false);

  // Add structured data for rich snippets
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "997cargo",
    "url": "https://997cargo.ru/",
    "logo": "https://i.ibb.co/9kCDBLTN/image.png",
    "description": "Организуем быструю доставку и надёжную логистику товаров из Китая для вашего бизнеса.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "ул. Примерная, д. 1",
      "addressLocality": "Москва",
      "postalCode": "123456",
      "addressCountry": "RU"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+7-999-123-4567",
      "contactType": "customer service",
      "availableLanguage": ["Russian", "English", "Chinese"]
    },
    "sameAs": [
      "https://facebook.com/kitlin",
      "https://twitter.com/kitlin",
      "https://instagram.com/kitlin"
    ]
  };

  // Add service structured data
  const serviceData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Доставка и экспедирование товаров из Китая",
    "provider": {
      "@type": "Organization",
      "name": "997cargo"
    },
    "serviceType": "Логистические услуги",
    "areaServed": {
      "@type": "Country",
      "name": "Россия"
    },
    "description": "Профессиональная доставка и экспедирование товаров из Китая. Комплексные логистические решения для бизнеса и частных лиц.",
    "offers": {
      "@type": "Offer",
      "price": "200",
      "priceCurrency": "CNY",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": "200",
        "priceCurrency": "CNY",
        "unitCode": "KGM",
        "unitText": "кг"
      }
    }
  };

  // Preload hero section background image
  React.useEffect(() => {
    const img = new Image();
    img.src = 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>997cargo - Организуем быструю доставку и надёжную логистику товаров из Китая для вашего бизнеса.</title>
        <meta name="description" content="Надёжная доставка и сопровождение грузов из Китая. Полный спектр логистических услуг для бизнеса. Более 10 лет на рынке и тысячи успешно выполненных заказов." />
        <meta name="keywords" content="доставка товаров из китая, экспедирование товаров из китая, логистика китай россия, карго из китая, поиск поставщиков в китае, таможенное оформление, грузоперевозки из китая" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-40 px-6 bg-gradient-to-b from-[#1B3BA0] to-[#0F2875] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0171E3]/10 via-transparent to-transparent"></div>
        </div>
        <div className="max-w-[980px] mx-auto relative text-center">
          <div className="max-w-[780px] mx-auto relative">
            <h1 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight">
              <span className="text-white block mb-2">Карго-доставка из Китая в Россию</span>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-[#99BBFF] block">
                быстро, надёжно, под ключ
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed mb-12">
              Осуществляем доставку товаров из Китая в Россию от 10 дней. Полный комплекс услуг: карго, экспедирование, таможенное оформление, поиск и выкуп товаров с китайских площадок.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => setShowRequestForm(true)}
                className="inline-flex items-center bg-white text-[#1B3BA0] px-8 py-4 rounded-full hover:bg-[#99BBFF] transition-all font-medium text-lg group shadow-lg hover:shadow-xl"
              >
                <Package2 className="w-5 h-5 mr-2" />
                Заказать доставку
                <ChevronRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => navigate('/prices')}
                className="inline-flex items-center bg-transparent text-white border-2 border-white/30 px-8 py-4 rounded-full hover:bg-white/10 transition-all font-medium text-lg group backdrop-blur-sm"
              >
                <Calculator className="w-5 h-5 mr-2" />
                Рассчитать стоимость
                <ChevronRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 px-6" style={{ contentVisibility: 'auto', containIntrinsicSize: '600px' }}>
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#1B3BA0] mb-16">Наши преимущества</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Package2 className="w-16 h-16 text-[#1B3BA0]" />,
                title: 'Полная логистика',
                description: 'Доставка от китайского склада до вашей двери с оформлением.'
              },
              {
                icon: <Shield className="w-16 h-16 text-[#1B3BA0]" />,
                title: 'Гарантия безопасности',
                description: 'Страховка и контроль груза на каждом этапе.'
              },
              {
                icon: <Clock className="w-16 h-16 text-[#1B3BA0]" />,
                title: 'Быстрая доставка',
                description: 'Сроки — от 10 дней по оптимальному маршруту.'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex flex-col items-center">
                  <div className="mb-6 bg-[#1B3BA0]/10 p-4 rounded-2xl">{feature.icon}</div>
                  <h3 className="text-xl font-bold text-[#1B3BA0] mb-4">{feature.title}</h3>
                  <p className="text-[#666] text-center">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Solutions */}
      <section className="py-24 px-6 bg-[#1B3BA0]/5" style={{ contentVisibility: 'auto', containIntrinsicSize: '600px' }}>
        <div className="max-w-[980px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#1B3BA0] mb-6">Решения для бизнеса</h2>
            <p className="text-xl text-[#666] max-w-2xl mx-auto">
              Комплексный подход к работе с китайскими поставщиками для компаний любого масштаба
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: <Building2 className="w-16 h-16 text-[#1B3BA0]" />,
                title: 'Для крупного бизнеса',
                features: [
                  'Персональный менеджер 24/7',
                  'Прямые контракты с фабриками',
                  'Особые условия доставки',
                  'Таможенное оформление'
                ]
              },
              {
                icon: <Users className="w-16 h-16 text-[#1B3BA0]" />,
                title: 'Для малого бизнеса',
                features: [
                  'Поиск надежных поставщиков',
                  'Выкуп товаров',
                  'Консолидация грузов',
                  'Оптимальные тарифы'
                ]
              }
            ].map((solution, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex flex-col items-center mb-8">
                  <div className="mb-6 bg-[#1B3BA0]/10 p-4 rounded-2xl">
                    {solution.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-[#1B3BA0]">{solution.title}</h3>
                </div>
                <ul className="space-y-4 bg-[#1B3BA0]/5 p-6 rounded-xl">
                  {solution.features.map((feature, i) => (
                    <li key={i} className="flex items-center text-[#666] font-medium">
                      <CheckCircle2 className="w-5 h-5 text-[#1B3BA0] mr-3 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#1B3BA0] mb-16">
            Наши услуги
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'ГРУЗОВЫЕ ПЕРЕВОЗКИ',
                description: 'Перевозим любые типы грузов — от сборных до негабаритных и проектных поставок.',
                timing: 'Сроки: индивидуально',
                image: 'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?auto=format&fit=crop&q=80'
              },
              {
                title: 'ОБМЕН ВАЛЮТЫ',
                description: 'Проводим безопасные трансферы любой валюты без комиссии и скрытых условий.',
                timing: 'От 5 дней',
                image: 'https://images.unsplash.com/photo-1580519542036-c47de6196ba5?auto=format&fit=crop&q=80'
              },
              {
                title: 'УСЛУГИ ПЕРЕВОДЧИКА',
                description: 'Предоставим профессионального переводчика для деловых переговоров и сопровождения.',
                timing: 'От 1 дня',
                image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&q=80'
              },
              {
                title: 'ТАМОЖЕННОЕ СОПРОВОЖДЕНИЕ',
                description: 'Оформим груз по всем правилам: от границы до склада. Быстро, прозрачно, легально.',
                timing: 'От 7 дней',
                image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&q=80'
              },
              {
                title: 'ПОИСК ПОСТАВЩИКОВ',
                description: 'Найдём надёжных производителей, проверим товары и организуем поставки.',
                timing: 'От 1 дня',
                image: 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?auto=format&fit=crop&q=80'
              },
              {
                title: 'ПРОВЕРКА КАЧЕСТВА',
                description: 'Личный контроль качества на фабрике. Мы на месте — вы спокойны.',
                timing: 'От 7 дней',
                image: 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?auto=format&fit=crop&q=80'
              },
              {
                title: 'ДОСТАВКА ОБРАЗЦОВ',
                description: 'Быстро отправим образцы для оценки качества, чтобы вы не теряли время.',
                timing: 'От 5 дней',
                image: 'https://images.unsplash.com/photo-1580674285054-bed31e145f59?auto=format&fit=crop&q=80'
              },
              {
                title: 'СОБСТВЕННЫЕ КОНТЕЙНЕРЫ',
                description: 'Предоставим личный контейнер для регулярных поставок с приоритетной отправкой.',
                timing: 'От 7 дней',
                image: 'https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?auto=format&fit=crop&q=80'
              },
              {
                title: 'КОНТРАКТНОЕ ПРОИЗВОДСТВО',
                description: 'Поможем создать товар под вашим брендом: от идеи до производства.',
                timing: 'От 14 дней',
                image: 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?auto=format&fit=crop&q=80'
              },
              {
                title: 'ВЫХОД НА МАРКЕТПЛЕЙСЫ',
                description: 'Проведем через все этапы выхода на площадки: регистрация, листинг, продажи.',
                timing: 'От 7–14 дней',
                image: 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?auto=format&fit=crop&q=80'
              },
              {
                title: 'СКЛАДСКОЕ ХРАНЕНИЕ',
                description: 'Обеспечим хранение ваших товаров на безопасных складах в Китае и РФ.',
                timing: 'От 1 дня',
                image: 'https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&q=80'
              },
              {
                title: 'АНАЛИЗ КОНКУРЕНТОВ',
                description: 'Покажем, как и что возят ваши конкуренты — от логистики до таможенных схем.',
                timing: 'От 2 часов до 1 дня',
                image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80'
              }
            ].map((service, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
                <div className="w-full h-48 rounded-xl overflow-hidden mb-6">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-xl font-bold text-[#1B3BA0] mb-4 h-[56px] flex items-center">{service.title}</h3>
                <div className="flex flex-col h-[200px]">
                  <p className="text-gray-600 mb-auto line-clamp-4">{service.description}</p>
                  <div className="mt-4">
                    <span className="text-sm text-gray-500">
                      <Clock className="w-4 h-4 inline-block mr-2" />
                      {service.timing}
                    </span>
                    <div className="mt-3">
                      <button
                        onClick={() => setShowRequestForm(true)}
                        className="text-[#1B3BA0] hover:text-[#162D7A] font-medium text-sm flex items-center"
                      >
                        ОСТАВИТЬ ЗАЯВКУ
                        <ChevronRight className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section for SEO */}
      <section className="py-24 px-6 bg-white" style={{ contentVisibility: 'auto', containIntrinsicSize: '600px' }}>
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-3xl font-semibold text-center text-[#1d1d1f] mb-12">
            Часто задаваемые вопросы
          </h2>
          
          {/* FAQ Accordion */}
          <div className="space-y-6">
            {[
              {
                question: 'Как организована доставка товаров из Китая?',
                answer: 'Доставка осуществляется автомобильным транспортом тремя основными способами:\n\nБыстрое авто — срок доставки 10-15 дней\nОбычное авто — срок доставки 15-20 дней\nМедленное авто — срок доставки 18-25 дней\n\nМы подбираем оптимальный способ доставки в зависимости от ваших приоритетов по срокам и бюджету. Все этапы логистики — от получения груза до прибытия в Россию — мы берем на себя.'
              },
              {
                question: 'Что такое экспедирование товаров из Китая?',
                answer: 'Экспедирование товаров в Китае — это комплекс мероприятий, направленных на обеспечение безопасной и своевременной отправки груза. В него входят:\n\n• проверка качества и комплектности товаров,\n• фото и видеофиксация,\n• контроль соответствия товара заявке,\n• упаковка и маркировка,\n• консолидация партий,\n• координация с поставщиком и складом.\n\nЭкспедирование позволяет минимизировать риски при доставке и убедиться, что товар соответствует ожиданиям до его отправки.'
              },
              {
                question: 'Куда приходит груз из Китая?',
                answer: 'По умолчанию груз прибывает на склад "Южные ворота" в Москве. По согласованию возможна доставка на склады "Люблино" или "Садовод".\n\nЕсли вы заказываете партию от 5 тонн или 30 кубов, доставка по Москве осуществляется бесплатно.'
              },
              {
                question: 'Как доставляется груз в регионы России?',
                answer: 'Груз сначала поступает на наш склад в Москве, после чего по согласованию с клиентом передаётся в транспортную компанию для дальнейшей доставки по регионам России.\nСтоимость доставки рассчитывается по тарифам выбранной транспортной компании.'
              },
              {
                question: 'Сколько стоит комиссия за выкуп и поиск товара?',
                answer: 'Комиссия зависит от сложности задачи и количества запросов:\n\n• Поиск и выкуп товара — бесплатно.\n• Если товар уже найден и требуется только оплата поставщику — услуга бесплатна.\n• Поиск контактов фабрики — фиксированная стоимость 200 юаней.\n\nМы всегда предлагаем прозрачные условия и обсуждаем стоимость до начала работы.'
              },
              {
                question: 'Сколько стоит упаковка?',
                answer: 'Упаковка подбирается в зависимости от типа товара, степени хрупкости и маршрута доставки. Ниже представлены основные варианты и стоимость:\n\n• Мешок + Скотч 5$\n• Угол+Мешок+Скотч 6$\n• Лента+Мешок+Скотч 7$\n• Деревянная обрешетка + Мешок + Скотч 10$\n• Палет 45$ куб\n\nПочему упаковка важна: При перевозке груз может промокнуть, попасть под тяжёлые коробки или получить повреждения. Поэтому грамотный выбор упаковки — это защита вашего товара от любых рисков в пути.'
              },
              {
                question: 'Сколько стоит доставка в Россию?',
                answer: 'Стоимость доставки зависит от категории товара, его плотности и выбранного типа доставки, поэтому назвать точную цену без уточнений невозможно.\n\nНа странице 👉 Цены мы показали примерную стоимость доставки по одной из популярных категорий (одежда) за 2024 год. Это поможет вам ориентироваться в диапазоне цен.\n\nЧтобы узнать актуальную цену именно для вашего груза — оставьте заявку, и наш менеджер свяжется с вами, уточнит параметры заказа и предоставит персональное коммерческое предложение в течение 5 минут.'
              }
            ].map((faq, index) => (
              <div
                key={index}
                className="bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
                onClick={() => {
                  const newExpandedFaqs = new Set(expandedFaqs);
                  if (newExpandedFaqs.has(index)) {
                    newExpandedFaqs.delete(index);
                  } else {
                    newExpandedFaqs.clear(); // Close other FAQs
                    newExpandedFaqs.add(index);
                  }
                  setExpandedFaqs(newExpandedFaqs);
                }}
              >
                <div className="p-6 cursor-pointer">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-[#1d1d1f] pr-8">
                      {faq.question}
                    </h3>
                    <ChevronRight 
                      className={`w-5 h-5 text-[#86868b] transition-transform duration-300 flex-shrink-0 ${
                        expandedFaqs.has(index) ? 'rotate-90' : ''
                      }`}
                    />
                  </div>
                  <div 
                    className={`transition-all duration-300 ${
                      expandedFaqs.has(index) ? 'mt-4 max-h-[1000px]' : 'max-h-0 overflow-hidden'
                    }`}
                  >
                    <p className="text-[#86868b] leading-relaxed whitespace-pre-line">
                      {faq.answer === 'Стоимость доставки зависит от категории товара, его плотности и выбранного типа доставки, поэтому назвать точную цену без уточнений невозможно.\n\nНа странице 👉 Цены мы показали примерную стоимость доставки по одной из популярных категорий (одежда) за 2024 год. Это поможет вам ориентироваться в диапазоне цен.\n\nЧтобы узнать актуальную цену именно для вашего груза — оставьте заявку, и наш менеджер свяжется с вами, уточнит параметры заказа и предоставит персональное коммерческое предложение в течение 5 минут.' ? (
                        <>
                          Стоимость доставки зависит от категории товара, его плотности и выбранного типа доставки, поэтому назвать точную цену без уточнений невозможно.
                          {'\n\n'}
                          На странице{' '}
                          <button 
                            onClick={() => navigate('/prices')}
                            className="text-[#1B3BA0] hover:text-[#0F2875] transition-colors inline-flex items-center"
                          >
                            👉 Цены
                          </button>
                          {' '}мы показали примерную стоимость доставки по одной из популярных категорий (одежда) за 2024 год. Это поможет вам ориентироваться в диапазоне цен.
                          {'\n\n'}
                          Чтобы узнать актуальную цену именно для вашего груза — {' '}
                          <button 
                            onClick={() => setShowRequestForm(true)}
                            className="text-[#1B3BA0] hover:text-[#0F2875] transition-colors inline-flex items-center"
                          >
                            оставьте заявку
                          </button>
                          , и наш менеджер свяжется с вами, уточнит параметры заказа и предоставит персональное коммерческое предложение в течение 5 минут.
                        </>
                      ) : faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-32 px-6 bg-[#1B3BA0]">
        <div className="max-w-[780px] mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Получить расчёт стоимости
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Заполните форму, и мы свяжемся с вами для обсуждения деталей
          </p>
          <RequestForm source="Главная страница" />
        </div>
      </section>
      
      {/* Request Form Popup */}
      {showRequestForm && (
        <RequestForm 
          isPopup 
          source="Главная страница"
          onClose={() => setShowRequestForm(false)} 
        />
      )}
    </div>
  );
}

export default HomePage;